// Conversión de una imagen (logo del comercio) a un comando ESC/POS raster
// monocromo (GS v 0), para imprimir el logo en el ticket Bluetooth.
//
// Usa <canvas> del navegador: carga la imagen, la escala al ancho del rollo, la
// umbraliza a 1 bit (blanco/negro) y empaqueta los bits en el formato GS v 0.
//
// Es BEST-EFFORT: si la imagen no carga o el canvas queda "tainted" por CORS
// (`getImageData` lanza), devuelve null y el ticket se imprime SIN logo (no se
// rompe la impresión). No se accede a `document`/`Image` en el top-level.

// Ancho en puntos del cabezal según el rollo (estándar: 8 dots/mm efectivos).
export const dotsForWidth = (pageWidth: 58 | 80 | undefined): number => (pageWidth === 58 ? 384 : 576);

// Empaqueta un buffer RGBA (getImageData) a un comando ESC/POS raster GS v 0.
//
// - Umbral 1-bit por luminancia (< 128 ⇒ pixel negro); transparente → blanco.
// - Bits MSB-first dentro de cada byte; `bytesPerRow = ceil(w / 8)`.
// - Header GS v 0: 0x1d 0x76 0x30 0x00 xL xH yL yH (m=0, xBytes=bytesPerRow).
//
// El alto `h` se codifica en 16 bits (máx 65535 pts), suficiente para cualquier
// ticket; no requiere troceo (el envío se trocea aparte vía writeChunked).
export const imageDataToEscPosRaster = (data: Uint8ClampedArray, w: number, h: number): Uint8Array => {
  const bytesPerRow = Math.ceil(w / 8);
  const raster = new Uint8Array(bytesPerRow * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      const alpha = data[i + 3];
      // Transparente → blanco; si no, luminancia. < 128 ⇒ pixel negro.
      const lum = alpha < 16 ? 255 : 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      if (lum < 128) raster[y * bytesPerRow + (x >> 3)] |= 0x80 >> (x & 7); // MSB-first
    }
  }
  const xBytes = bytesPerRow;
  const header = new Uint8Array([
    0x1d,
    0x76,
    0x30,
    0x00, // GS v 0 m=0
    xBytes & 0xff,
    (xBytes >> 8) & 0xff, // ancho en bytes
    h & 0xff,
    (h >> 8) & 0xff, // alto en puntos
  ]);
  const out = new Uint8Array(header.length + raster.length);
  out.set(header);
  out.set(raster, header.length);
  return out;
};

// Devuelve el comando GS v 0 (raster) de un canvas ya dibujado (negro sobre
// blanco), o null si el canvas está "tainted" por CORS (`getImageData` lanza) o
// no hay contexto 2D. El renderer del ticket-canvas usa esto.
export const canvasToEscPosRaster = (canvas: HTMLCanvasElement): Uint8Array | null => {
  const w = canvas.width;
  const h = canvas.height;
  if (w <= 0 || h <= 0) return null;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  try {
    const { data } = ctx.getImageData(0, 0, w, h); // lanza si está tainted por CORS
    return imageDataToEscPosRaster(data, w, h);
  } catch {
    return null;
  }
};

const loadImageEl = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('No se pudo cargar la imagen del logo.'));
    img.src = url;
  });

// Carga una imagen para dibujarla en <canvas> SIN depender del CORS del canvas.
//
// Vía robusta: `fetch(mode:'cors')` → `blob` → `createImageBitmap`. Un bitmap creado
// desde un blob YA descargado NO "taintea" el canvas (el blob es un recurso local),
// aunque el origen sea cross-origin. Además `cache:'reload'` evita reutilizar la
// entrada de caché "envenenada" por el `<img>` SIN `Origin` que pinta el NavBar (que
// dejaba en caché la variante de la respuesta sin `Access-Control-Allow-Origin`).
//
// Fallback: `<img crossOrigin>` (que SÍ puede taintear) — el caller debe AISLAR/PROBAR
// el resultado (dibujarlo en un canvas aparte y probar getImageData) para no romper.
export const loadDrawableImage = async (url: string): Promise<ImageBitmap | HTMLImageElement> => {
  if (typeof fetch !== 'undefined' && typeof createImageBitmap === 'function') {
    try {
      const res = await fetch(url, { mode: 'cors', cache: 'reload' });
      if (res.ok) {
        const blob = await res.blob();
        return await createImageBitmap(blob);
      }
    } catch {
      /* cae al <img crossOrigin> */
    }
  }
  return loadImageEl(url);
};

// Devuelve el comando GS v 0 (raster) del logo escalado a `maxWidthDots`, o null
// si la imagen no es accesible/convertible. Ya está aislado (canvas propio + try/catch).
export const imageToEscPosRaster = async (url: string, maxWidthDots: number): Promise<Uint8Array | null> => {
  if (typeof document === 'undefined' || typeof Image === 'undefined') return null;
  try {
    const img = await loadDrawableImage(url);
    const ratio = Math.min(1, maxWidthDots / (img.width || maxWidthDots));
    const w = Math.max(1, Math.round((img.width || maxWidthDots) * ratio));
    const h = Math.max(1, Math.round((img.height || 1) * ratio));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    const { data } = ctx.getImageData(0, 0, w, h); // lanza si está tainted por CORS
    return imageDataToEscPosRaster(data, w, h);
  } catch {
    return null;
  }
};
